export function isEqual<T>(a: T, b: T): boolean {
  if (a === b) return true;

  if (a === null || b === null || typeof a !== typeof b) return false;

  // Handle Date
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime();
  }

  // Handle Set
  if (a instanceof Set && b instanceof Set) {
    if (a.size !== b.size) return false;
    for (const val of a) {
      if (!b.has(val)) return false;
    }
    return true;
  }

  // Handle Map
  if (a instanceof Map && b instanceof Map) {
    if (a.size !== b.size) return false;
    for (const [key, valA] of a) {
      if (!b.has(key)) return false;
      const valB = b.get(key);
      if (!isEqual(valA, valB)) return false;
    }
    return true;
  }

  // Handle Array (an array is never equal to a plain object with the same keys)
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!isEqual(a[i], b[i])) return false;
    }
    return true;
  }

  // Handle object
  if (typeof a === 'object' && typeof b === 'object') {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;

    for (const key of aKeys) {
      // eslint-disable-next-line no-prototype-builtins
      if (!b.hasOwnProperty(key)) return false;
      if (!isEqual(a[key as keyof T], b[key as keyof T])) return false;
    }
    return true;
  }

  return false;
}

const toPathSegments = (path: string): string[] => path.split('.').filter(Boolean);

export const getValueByPath = (source: unknown, path: string): unknown =>
  toPathSegments(path).reduce<unknown>((current, segment) => {
    if (current === null || typeof current !== 'object') return undefined;
    return (current as Record<string, unknown>)[segment];
  }, source);

export const hasValueByPath = (source: unknown, path: string): boolean => {
  const segments = toPathSegments(path);
  if (!segments.length) return false;

  let current: unknown = source;

  for (const segment of segments) {
    if (current === null || typeof current !== 'object') return false;
    if (!(segment in (current as Record<string, unknown>))) return false;
    current = (current as Record<string, unknown>)[segment];
  }

  return true;
};

export const setValueByPath = <T extends object>(target: T, path: string, value: unknown): T => {
  const segments = toPathSegments(path);
  if (!segments.length) return target;

  let current = target as Record<string, unknown>;

  segments.forEach((segment, index) => {
    if (index === segments.length - 1) {
      current[segment] = value;
      return;
    }

    const existing = current[segment];
    if (existing === null || typeof existing !== 'object') {
      current[segment] = /^\d+$/.test(segments[index + 1]) ? [] : {};
    }
    current = current[segment] as Record<string, unknown>;
  });

  return target;
};
